import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import MartyLink from 'components/common/MartyLink';
import FinalSale from 'zen/components/common/FinalSale';
import { generateProductUrl, isItemInCancelState, isItemInReturnState } from 'helpers/MyAccountUtils';
import css from 'styles/components/account/orderProduct.scss';
import { IS_GIFT_CARD } from 'common/regex';

const OrderProduct = ({ product, price, isLegacy, finalSale, lineItemState, hideWriteReview, itemUniqueId }, { testId, marketplace }) => {
  const { attributes, retinaImageUrl, name, brand, style, reviewUrl } = product;
  const returnState = isItemInReturnState(lineItemState);
  const cancelState = isItemInCancelState(lineItemState);
  const isInSpecialState = returnState || cancelState;
  const { features: { showReviews }, checkout: { asinVerbiage = 'ASIN' } } = marketplace;
  const isGiftCard = IS_GIFT_CARD.test(brand);

  return (
    <div className={css.wrapper} data-test-id={testId('orderProduct')}>
      <MartyLink
        // Since we have a duplicate link wrapping both this purely decorative image and
        // The Brand/Product itself, we can safely hide this from screen readers/keyboard nav
        aria-hidden={true}
        tabIndex={-1}
        to={generateProductUrl(product, isLegacy)}
        className={css.mProduct}
        data-track-action="My-Favorites"
        data-track-label="Favorites-Product"
        data-track-value="Image-Clicked"
        data-test-id={testId('productImageLink')}>
        <div className={cn(css.image,
          isInSpecialState && css.specialState,
          returnState && css.returnedState,
          cancelState && css.cancelledState)}>
          { isInSpecialState && <span>{lineItemState}</span> }
          <img src={retinaImageUrl} alt={name}/>
        </div>
      </MartyLink>

      <div id={`aria-orderProductDetails-${itemUniqueId}`} className={css.itemContainer}>
        <MartyLink
          className={css.productTitle}
          to={generateProductUrl(product, isLegacy)}
          data-test-id={testId('productNameLink')}>
          <p data-test-id={testId('productBrand')}>{brand}</p>
          <p className={css.productName} data-test-id={testId('productStyle')}>{style}</p>
        </MartyLink>
        <div className={css.itemPrice} data-test-id={testId('productPrice')}>{price}</div>
        <div className={css.itemDetails}>
          {attributes.map(({ key, value }) => (
            !(key === 'Color' && isGiftCard) &&
              <dl key={key}>
                <dt data-test-id={testId(`attribute-${key}`)}>{key === 'ASIN' ? asinVerbiage : key}:</dt><dd>{value}</dd>
              </dl>
          ))}
          {finalSale && <FinalSale />}
          {showReviews && reviewUrl && !hideWriteReview &&
            <p className={css.reviewUrl} data-test-id={testId('productReviewLink')}>
              <MartyLink to={reviewUrl}>Write a Review</MartyLink>
            </p>}
        </div>
      </div>
    </div>
  );
};

OrderProduct.contextTypes = {
  testId: PropTypes.func,
  marketplace: PropTypes.object
};

OrderProduct.propTypes = {
  product: PropTypes.object.isRequired,
  price: PropTypes.string
};

export default OrderProduct;
